import * as _ from 'lodash';
import * as moment from 'moment';
import usePrevious from '@nkr/services/use-previous';
import { IBaseResponse, IExpertSystemOrderByRecipient } from '@nkr/models/index';
import { ExpertSystemStatus } from '@nkr/enums/index';
import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap'
import { Action, ActionStatus, IExpertSystemOrderSummaryWithComments, IExpertSystemRecipientDonor } from './models';
import { TableWrapper } from './styles';
import { IExpertSystemOrderResponse } from '@nkr/store/index';
import RecipientDonorsModalRow from './recipient-donors-modal-row';

interface IProps {
    show: boolean,
    data: IExpertSystemOrderSummaryWithComments,
    orders: IExpertSystemOrderByRecipient[],
    centerId: number,
    onUpdateExpertSystemOrder: (orderId: number, statusId: number, hideLoading: boolean) => Promise<IExpertSystemOrderResponse>,
    doStandardVoucher: (donorId: number, recipientId: number) => Promise<IBaseResponse>,
    doCompatiblePair: (donorId: number, recipientId: number, direct: boolean, surgeryDate: string) => Promise<IBaseResponse>,
    onRefreshList: () => void,
    onClose: () => void
}

const RecipientDonorsModal = (props: IProps) => {
    const [list, setList] = useState<IExpertSystemRecipientDonor[]>([]);
    const prevShow = usePrevious(props.show);

    useEffect(() => {
        if (props.show == true && prevShow == false) {
            setList(props.orders.map(x => (
                {
                    ...x,
                    standardVoucher: ActionStatus.Pending,
                    kpdPair: ActionStatus.Pending,
                    internalDirect: ActionStatus.Pending,
                    internalDirectSurgeryDate: '',
                    validInternalDirectSurgeryDate: true,
                    withdrawDonor: ActionStatus.Pending
                }
            )));
        }
    }, [props.show]);

    const handleAction = (action: Action, orderId: number) => {
        let order = list.find(x => x.id == orderId);
        let { recipientId } = order;

        if (action == Action.Evaluate) {
            let url = `center/${props.centerId}/action-center/expert-system/view-order/${orderId}`;
            let win = window.open(url, '_blank');
            win.focus();
        } else if (action == Action.PotentialDonors) {
            let url = `/center/${props.centerId}/recipient/${recipientId}/potential-donors`;
            let win = window.open(url, '_blank');
            win.focus();
        } else {
            confirmAction(orderId, action);
        }
    }

    const handleProcessAction = (orderId: number, action: Action, cancel: boolean) => {
        if (action == Action.StartStandardVoucher) {
            processStandardVoucher(orderId, cancel);
        }

        if (action == Action.StartKpdPair) {
            processKpdPair(orderId, cancel);
        }

        if (action == Action.StartInternalDirect) {
            processInternalDirect(orderId, cancel);
        }

        if (action == Action.WithdrawDonor) {
            processWithdrawDonor(orderId, cancel);
        }
    };

    const handleDismissAlert = (orderId: number, action: Action) => {
        let newList = _.cloneDeep(list).map(x => {
            if (x.id == orderId) {
                if (action == Action.StartStandardVoucher) {
                    x.standardVoucher = ActionStatus.Dismissed;
                }

                if (action == Action.StartKpdPair) {
                    x.kpdPair = ActionStatus.Dismissed;
                }

                if (action == Action.StartInternalDirect) {
                    x.internalDirect = ActionStatus.Dismissed;
                }
            }

            return x;
        });

        setList(newList);
    };

    const handleSurgeryDateChange = (orderId: number, surgeryDate: string) => {
        let newList = _.cloneDeep(list).map(x => {
            if (x.id == orderId) {
                x.internalDirectSurgeryDate = surgeryDate;
            }

            return x;
        });

        setList(newList);
    };

    const confirmAction = (orderId: number, action: Action) => {
        let newList = _.cloneDeep(list).map(x => {
            if (x.id == orderId) {
                if (action == Action.StartStandardVoucher) {
                    x.standardVoucher = ActionStatus.Confirm;
                }

                if (action == Action.StartKpdPair) {
                    x.kpdPair = ActionStatus.Confirm;
                }

                if (action == Action.StartInternalDirect) {
                    x.internalDirect = ActionStatus.Confirm;
                }

                if (action == Action.WithdrawDonor) {
                    x.withdrawDonor = ActionStatus.Confirm;
                }
            }

            return x;
        });

        setList(newList);
    };

    const processStandardVoucher = async (orderId: number, cancel: boolean) => {
        let newList = _.cloneDeep(list);
        let order = newList.find(x => x.id == orderId);

        if (cancel) {
            order.standardVoucher = ActionStatus.Pending;
        } else {
            let { donorId, recipientId } = order;

            let expertSystemResponse = await props.onUpdateExpertSystemOrder(orderId, ExpertSystemStatus.Completed, false);
            let svResponse = await props.doStandardVoucher(donorId, recipientId);

            if (expertSystemResponse.data.success && svResponse.data.success) {
                order.standardVoucher = ActionStatus.Completed;
            }
        }

        setList(newList);
    };

    const processKpdPair = async (orderId: number, cancel: boolean) => {
        let newList = _.cloneDeep(list);
        let order = newList.find(x => x.id == orderId);

        if (cancel) {
            order.kpdPair = ActionStatus.Pending;
        } else {
            let { donorId, recipientId } = order;

            let expertSystemResponse = await props.onUpdateExpertSystemOrder(orderId, ExpertSystemStatus.Completed, false);
            let cpResponse = await props.doCompatiblePair(donorId, recipientId, false, '');

            if (expertSystemResponse.data.success && cpResponse.data.success) {
                order.kpdPair = ActionStatus.Completed;
            }
        }

        setList(newList);
    };

    const processInternalDirect = async (orderId: number, cancel: boolean) => {
        let newList = _.cloneDeep(list);
        let order = newList.find(x => x.id == orderId);

        if (cancel) {
            order.internalDirect = ActionStatus.Pending;
            order.validInternalDirectSurgeryDate = true;
        } else {
            let { donorId, recipientId, internalDirectSurgeryDate } = order;

            let validSurgeryDate = moment(internalDirectSurgeryDate, 'MM/DD/YYYY').isValid();

            if (validSurgeryDate) {
                let expertSystemResponse = await props.onUpdateExpertSystemOrder(orderId, ExpertSystemStatus.Completed, false);
                let cpResponse = await props.doCompatiblePair(donorId, recipientId, true, internalDirectSurgeryDate);

                if (expertSystemResponse.data.success && cpResponse.data.success) {
                    order.internalDirect = ActionStatus.Completed;
                }
            } else {
                order.validInternalDirectSurgeryDate = false;
            }
        }

        setList(newList);
    };

    const processWithdrawDonor = async (orderId: number, cancel: boolean) => {
        let newList = _.cloneDeep(list);
        let order = newList.find(x => x.id == orderId);

        if (cancel) {
            order.withdrawDonor = ActionStatus.Pending;
        } else {
            let expertSystemResponse = await props.onUpdateExpertSystemOrder(orderId, ExpertSystemStatus.Cancelled, false);

            if (expertSystemResponse.data.success) {
                props.onRefreshList();
                order.withdrawDonor = ActionStatus.Completed;
            }

            newList = newList.filter(x => x.id != orderId);
        }

        setList(newList);
    };

    if (props.data == null)
        return null;

    return (
        <Modal backdrop='static' bsSize='lg' show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                Donors for Recipient <span style={{ fontWeight: 'bold', fontSize: 16 }}><a target='_blank' href={`center/${props.centerId}/recipient/${props.data.recipientId}/profile/general`}>{props.data.recipientAlias}</a></span>
            </Modal.Header>
            <Modal.Body>
                <TableWrapper condensed>
                    <thead>
                        <tr>
                            <th>R#</th>
                            <th>Donor<br />Center</th>
                            <th>Donor<br />Alias</th>
                            <th>Donor<br />ABO</th>
                            <th>Recipient<br />ABO</th>
                            <th>Eplet<br />MM</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((order, index) =>
                            <RecipientDonorsModalRow
                                key={'es-donor-' + index}
                                index={index}
                                order={order}
                                recipientAbo={props.data.recipientAbo}
                                centerId={props.centerId}
                                onAction={handleAction}
                                onProcessAction={handleProcessAction}
                                onDismissAlert={handleDismissAlert}
                                onSurgeryDateChange={handleSurgeryDateChange}
                            />
                        )}
                    </tbody>
                </TableWrapper>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' onClick={props.onClose}>Close</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default RecipientDonorsModal